import React from 'react';
import { Grid, TextField, Typography } from '@mui/material';
import { FormikProps } from 'formik';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

interface Props {
  formik: FormikProps<any>;
  title?: string;
}

const HomeAddressSection: React.FC<Props> = ({ formik, title = "Home Address" }) => (
  <>
    <Grid item xs={12}>
      <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>{title}</Typography>
    </Grid>

    <Grid item xs={12}>
      <TextField
        fullWidth
        id="homeAddressLine1"
        name="homeAddressLine1"
        label="Address Line 1"
        value={formik.values.homeAddressLine1 || ''}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={formik.touched.homeAddressLine1 && Boolean(formik.errors.homeAddressLine1)}
        helperText={formik.touched.homeAddressLine1 && formik.errors.homeAddressLine1 as string}
        sx={{
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            '& fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.2)',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.3)',
            },
            '&.Mui-focused fieldset': {
              borderColor: 'primary.main',
            },
          },
          '& .MuiInputLabel-root': {
            color: 'rgba(255, 255, 255, 0.7)',
          },
          '& .MuiInputBase-input': {
            color: 'white',
          },
          '& .MuiFormHelperText-root': {
            color: 'rgba(255, 255, 255, 0.7)',
          },
        }}
      />
    </Grid>

    <Grid item xs={12}>
      <TextField
        fullWidth
        id="homeAddressLine2"
        name="homeAddressLine2"
        label="Address Line 2 (Optional)"
        value={formik.values.homeAddressLine2 || ''}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        sx={{
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            '& fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.2)',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.3)',
            },
            '&.Mui-focused fieldset': {
              borderColor: 'primary.main',
            },
          },
          '& .MuiInputLabel-root': {
            color: 'rgba(255, 255, 255, 0.7)',
          },
          '& .MuiInputBase-input': {
            color: 'white',
          },
        }}
      />
    </Grid>

    <Grid item xs={12} sm={6}>
      <TextField
        fullWidth
        id="homeCity"
        name="homeCity"
        label="City/Town"
        value={formik.values.homeCity || ''}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={formik.touched.homeCity && Boolean(formik.errors.homeCity)}
        helperText={formik.touched.homeCity && formik.errors.homeCity as string}
        sx={{
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            '& fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.2)',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.3)',
            },
            '&.Mui-focused fieldset': {
              borderColor: 'primary.main',
            },
          },
          '& .MuiInputLabel-root': {
            color: 'rgba(255, 255, 255, 0.7)',
          },
          '& .MuiInputBase-input': {
            color: 'white',
          },
          '& .MuiFormHelperText-root': {
            color: 'rgba(255, 255, 255, 0.7)',
          },
        }}
      />
    </Grid>

    <Grid item xs={12} sm={6}>
      <TextField
        fullWidth
        id="homeCounty"
        name="homeCounty"
        label="County"
        value={formik.values.homeCounty || ''}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={formik.touched.homeCounty && Boolean(formik.errors.homeCounty)}
        helperText={formik.touched.homeCounty && formik.errors.homeCounty as string}
        sx={{
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            '& fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.2)',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.3)',
            },
            '&.Mui-focused fieldset': {
              borderColor: 'primary.main',
            },
          },
          '& .MuiInputLabel-root': {
            color: 'rgba(255, 255, 255, 0.7)',
          },
          '& .MuiInputBase-input': {
            color: 'white',
          },
          '& .MuiFormHelperText-root': {
            color: 'rgba(255, 255, 255, 0.7)',
          },
        }}
      />
    </Grid>

    <Grid item xs={12} sm={6}>
      <TextField
        fullWidth
        id="homePostcode"
        name="homePostcode"
        label="Postcode"
        value={formik.values.homePostcode || ''}
        onChange={(e) => formik.setFieldValue('homePostcode', e.target.value.toUpperCase())}
        onBlur={formik.handleBlur}
        error={formik.touched.homePostcode && Boolean(formik.errors.homePostcode)}
        helperText={formik.touched.homePostcode && formik.errors.homePostcode as string}
        sx={{
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            '& fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.2)',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.3)',
            },
            '&.Mui-focused fieldset': {
              borderColor: 'primary.main',
            },
          },
          '& .MuiInputLabel-root': {
            color: 'rgba(255, 255, 255, 0.7)',
          },
          '& .MuiInputBase-input': {
            color: 'white',
          },
          '& .MuiFormHelperText-root': {
            color: 'rgba(255, 255, 255, 0.7)',
          },
        }}
      />
    </Grid>

    <Grid item xs={12} sm={6}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <DatePicker
          label="Moved In Date"
          value={formik.values.homeMovedInDate || null}
          onChange={(newValue) => formik.setFieldValue('homeMovedInDate', newValue)}
          format="dd/MM/yyyy"
          maxDate={new Date()}
          slotProps={{
            textField: {
              fullWidth: true,
              error: formik.touched.homeMovedInDate && Boolean(formik.errors.homeMovedInDate),
              helperText: (formik.touched.homeMovedInDate && formik.errors.homeMovedInDate as string) || 'When did you move to this address?',
              sx: {
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  '& fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'primary.main',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(255, 255, 255, 0.7)',
                },
                '& .MuiInputBase-input': {
                  color: 'white',
                },
                '& .MuiFormHelperText-root': {
                  color: 'rgba(255, 255, 255, 0.7)',
                },
              }
            },
            popper: {
              sx: {
                '& .MuiPaper-root': {
                  backgroundColor: 'rgba(30, 41, 59, 0.95)',
                  backdropFilter: 'blur(12px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  '& .MuiPickersDay-root': {
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)'
                    },
                    '&.Mui-selected': {
                      backgroundColor: 'primary.main'
                    }
                  },
                  '& .MuiPickersCalendarHeader-root': {
                    color: 'white'
                  },
                  '& .MuiPickersArrowSwitcher-button': {
                    color: 'white'
                  },
                  '& .MuiDayCalendar-weekDayLabel': {
                    color: 'rgba(255, 255, 255, 0.7)'
                  }
                }
              }
            },
          }}
        />
      </LocalizationProvider>
    </Grid>
  </>
);

export default HomeAddressSection;
